import { useRef, useState } from 'react';
import { Link, Navigate, useParams, useNavigate } from 'react-router-dom';
import { flushSync } from 'react-dom';
import ParticipationApiModel from '../../models/participation.api-model';
import useFetch, { FetchConfig, Status } from '../../../../hooks/fetch.hook';
import Spinner from '../../../Shared/Spinner';
import ValidationSummary from '../../../Shared/ValidationSummary';
import TextFieldFormGroup, { type ITextFieldFormGroupState } from '../../../Shared/TextFieldFormGroup';
import ValidationInfo from '../../../../validation/validation-info';
import type IValidatable from '../../../Shared/validatable.interface';
import { ValidatableComponent, ValidationHelper } from '../../../../helpers/validation.helper';
import { AnalyticsService } from '../../../../services/analytics-service';
import type { IServerValidationError } from '../../../Shared/validatable.interface';
import Alert from '../../../Shared/Alert';
import Breadcrumb from '../Breadcrumb';
import PageIntroduction from '../../Shared/PageIntroduction';
import { TeacherApiModel } from '../../models/teacher.api-model';
import type EditParticipationApiModel from './edit-participation.api-model';
import { ParticipationHelper } from '../../helpers/participation.helper';
import InformationBox from '../../Shared/InformationBox';
import SelectSchool, { type ISelectSchoolState } from '../SelectSchool';
import { Role } from '../../../../models/pa-egna-ben/role.enum';
import FocusTrap from '../../../Shared/FocusTrap';
import type IParticipation from '../../../../models/pa-egna-ben/participation.model';
import { TranslationService } from '../../../../services/translation.service';
import NotFoundRedirect from '../NotFoundRedirect';
import { SelectSchoolField, SelectSchoolMode } from '../SelectSchool/select-school.enum';
import { InformationBoxType } from '../InformationBox/information-box-type.enum';
import { IUser } from '../../../../models/pa-egna-ben/user.model';
import LogService from '../../../../services/log-service/log.service';
import AntiforgeryService from '../../../../services/anti-forgery.service';

function EditParticipation({ viewerRole }: { viewerRole: Role }) {
  // === PARAMS ===
  const { participationId } = useParams();
  const navigate = useNavigate();

  // === STATE ===
  const [edited, setEdited] = useState<number>(0);
  const [teachersEdited, setTeachersEdited] = useState<number>(0);
  const [{ status: getParticipationStatus, data: getParticipationData, httpResponseStatusCode: participationsHttpResponseStatusCode }] = useFetch<ParticipationApiModel>(
    `/api/paegnaben/${viewerRole}/participations/${participationId}`,
    false,
    false,
    FetchConfig.default,
    [edited],
  );
  const [{ status: getTeachersStatus, data: getTeachersData }] = useFetch<TeacherApiModel[]>(
    `/api/paegnaben/${viewerRole}/participations/${participationId}/teachers`,
    false,
    false,
    FetchConfig.default,
    [teachersEdited],
  );

  const [nameState, setNameState] = useState<ITextFieldFormGroupState>({ value: '', validationInfo: new ValidationInfo() });
  const [numberOfStudentsState, setNumberOfStudentsState] = useState<ITextFieldFormGroupState>({ value: '', validationInfo: new ValidationInfo() });
  const [selectSchoolState, setSelectSchoolState] = useState<ISelectSchoolState>({
    value: {
      mode: SelectSchoolMode.chooseinlist,
      school: {
        id: -1,
        name: '',
        streetAddress: '',
        postalCode: '',
        city: '',
        municipalityGid: '',
      },
    },
    validationInfo: new ValidationInfo(),
  });
  const [saveStatus, setSaveStatus] = useState<Status>(Status.notperformed);
  const [saveErrorCode, setSaveErrorCode] = useState<number | undefined>(undefined);
  const [selectedParticipation, setSelectedParticipation] = useState<IParticipation | undefined>(undefined);
  const [selectedTeacher, setSelectedTeacher] = useState<IUser | undefined>(undefined);
  const [removedTeacher, setRemovedTeacher] = useState<IUser | undefined>(undefined);
  const [deleteParticipationStatus, setDeleteParticipationStatus] = useState<{ status: Status; statusCode: number | undefined }>({ status: Status.notperformed, statusCode: 0 });

  // === REFS ===
  const nameRef = useRef<IValidatable>(null!);
  const numberOfStudentsRef = useRef<IValidatable>(null!);
  const schoolRef = useRef<IValidatable>(null!);

  // === INTERFACE IMPLEMENTATION ===
  function getValidatableComponents(): {
    component: IValidatable;
    validationInfo: ValidationInfo;
    setState: React.Dispatch<React.SetStateAction<{ value: unknown; validationInfo: ValidationInfo }>>;
  }[] {
    const result: ValidatableComponent[] = [
      { component: nameRef.current, validationInfo: nameState.validationInfo, setState: setNameState },
      { component: numberOfStudentsRef.current, validationInfo: numberOfStudentsState.validationInfo, setState: setNumberOfStudentsState },
      { component: schoolRef.current, validationInfo: selectSchoolState.validationInfo, setState: setSelectSchoolState },
    ];

    return result;
  }

  // === VARIABLES ===
  const validationSummaryErrors: string[] = ValidationHelper.getValidationSummaryErrors(getValidatableComponents());
  const participation = getParticipationData ? ParticipationApiModel.mapToBusinessModel(getParticipationData) : undefined;
  const teachers = getTeachersData?.map(TeacherApiModel.mapToBusinessModel);

  function handleServerValidationErrors(errors: IServerValidationError[]): void {
    const invalidValidationInfos = ValidationHelper.handleServerValidationErrors(errors, getValidatableComponents());
    ValidationHelper.trackValidationErrors(invalidValidationInfos, (e) => {
      AnalyticsService.addEvent(AnalyticsService.PaEgnaBenTeacher, AnalyticsService.ErrorAction, e);
    });
  }

  // === EVENT HANDLERS ===
  async function handleSaveButtonClick(ev: React.MouseEvent<HTMLButtonElement>) {
    ev.preventDefault();
    let isFormValid: boolean | undefined;
    let invalidValidationInfos: ValidationInfo[];
    let submitResult: { status: Status; errorCode: number | undefined } | undefined;
    await flushSync(async () => {
      if (saveStatus === Status.loading) return;
      setSaveErrorCode(undefined);
      [isFormValid, invalidValidationInfos] = ValidationHelper.checkValidity(getValidatableComponents());
      if (!isFormValid) {
        ValidationHelper.trackValidationErrors(invalidValidationInfos, (e) => {
          AnalyticsService.addEvent(AnalyticsService.PaEgnaBenAdmin, AnalyticsService.ErrorAction, e);
        });
        return;
      }
      setSaveStatus(Status.loading);
      const editParticipationApiModel: EditParticipationApiModel = {
        name: nameState.value,
        numberOfStudents: numberOfStudentsState.value ? parseInt(numberOfStudentsState.value, 10) : 0,
        schoolId: viewerRole === Role.municipalityAdministrator ? selectSchoolState.value.school?.id : undefined,
      };
      try {
        const response = await fetch(`/api/paegnaben/${viewerRole}/participations/${participationId}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest', 'X-XSRF-TOKEN': await AntiforgeryService.getToken() },
          body: JSON.stringify(editParticipationApiModel),
        });
        if (response.ok) {
          setSaveStatus(Status.success);
          setSaveErrorCode(undefined);
          setEdited(edited + 1);
          submitResult = { status: Status.success, errorCode: undefined };
          AnalyticsService.addEvent(AnalyticsService.PaEgnaBenTeacher, 'Klass ändrad', '');
        } else {
          setSaveStatus(Status.failed);
          setSaveErrorCode(response.status);
          submitResult = { status: Status.failed, errorCode: response.status };
          if (response.status === 400) {
            handleServerValidationErrors((await response.json()).errors);
          }
          AnalyticsService.addEvent(AnalyticsService.PaEgnaBenTeacher, 'Klass ändrad - misslyckat', '');
        }
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } catch (error: any) {
        setSaveStatus(Status.failed);
        submitResult = { status: Status.failed, errorCode: error.status };
      }
    });
    if (!isFormValid || submitResult?.errorCode === 400) {
      document.getElementById('form')?.scrollIntoView();
      document.getElementById('form-validation-summary')?.focus();
    }
  }

  async function handleConfirmDeleteClick() {
    if (!selectedParticipation) return;

    try {
      setDeleteParticipationStatus({ status: Status.notperformed, statusCode: 0 });
      const response = await fetch(`/api/paegnaben/municipality-administrator/participations/${selectedParticipation.id}`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest', 'X-XSRF-TOKEN': await AntiforgeryService.getToken() },
      });

      setDeleteParticipationStatus({ status: response.ok ? Status.success : Status.failed, statusCode: response.status });
      if (response.ok) {
        AnalyticsService.addEvent(AnalyticsService.PaEgnaBenTeacher, 'Klass borttagen', '');
        navigate(participation ? `/kommuner/${participation.school?.municipalityGid}?dp=${participation.id}` : '/', { state: participation });
      } else {
        AnalyticsService.addEvent(AnalyticsService.PaEgnaBenTeacher, 'Klass borttagen - misslyckat', '');
      }
      setSelectedParticipation(undefined);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      setDeleteParticipationStatus({ status: Status.failed, statusCode: error.status });
    }
  }

  async function handleRemoveTeacherClick(teacher: IUser) {
    if (!selectedTeacher) return;

    try {
      const response = await fetch(`/api/paegnaben/${viewerRole}/participations/${participation?.id}/remove-teacher`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest', 'X-XSRF-TOKEN': await AntiforgeryService.getToken() },
        body: JSON.stringify({ emailAddress: teacher.emailAddress }),
      });
      if (response.ok) {
        setRemovedTeacher(teacher);
        setSelectedTeacher(undefined);
        setTeachersEdited(edited + 1);
        AnalyticsService.addEvent(AnalyticsService.PaEgnaBenTeacher, 'Pedagog borttagen', '');
      } else {
        setRemovedTeacher(undefined);
        setSelectedTeacher(undefined);
        AnalyticsService.addEvent(AnalyticsService.PaEgnaBenTeacher, 'Pedagog borttagen - misslyckat', '');
      }
    } catch (error: unknown) {
      LogService.logError(error, 'Kunde inte ta bort pedagog');
    }
  }

  if (getParticipationStatus === Status.success && nameState.value === '' && numberOfStudentsState.value === '' && participation) {
    setNameState({ value: participation?.name, validationInfo: new ValidationInfo() });
    setNumberOfStudentsState({ value: participation?.numberOfStudents.toString(), validationInfo: new ValidationInfo() });
  }

  function handleOnClose() {
    setSelectedParticipation(undefined);
    setSelectedTeacher(undefined);
  }

  // === SIDE EFFECTS ===
  if (getParticipationData && selectSchoolState.value.school?.id === -1) {
    setSelectSchoolState((prevState) => ({
      ...prevState,
      value: {
        ...prevState.value,
        school: {
          id: participation?.school?.id ?? -1,
          name: participation?.school?.name ?? '',
          streetAddress: participation?.school?.streetAddress ?? '',
          postalCode: participation?.school?.postalCode ?? '',
          city: participation?.school?.city ?? '',
          municipalityGid: participation?.school?.municipalityGid ?? '',
        },
      },
    }));
  }

  // === RENDER ===
  if (saveStatus === Status.success) {
    const queryParams = new URLSearchParams({ a: 'klass-sparad' }).toString();
    return <Navigate to={`/klasser/${participationId}?${queryParams}`} />;
  }
  if (saveStatus === Status.failed || getParticipationStatus === Status.failed) {
    if (saveErrorCode === 401 || participationsHttpResponseStatusCode === 401) {
      return <Navigate to="/session-utgangen" />;
    }
    if (saveErrorCode === 403 || participationsHttpResponseStatusCode === 403) {
      return <Navigate to="/atkomst-saknas" />;
    }
    if (participationsHttpResponseStatusCode === 404) {
      return <NotFoundRedirect />;
    }
  }

  function renderPreventDeleteModalContent() {
    return (
      <div className="modal-content">
        <div className="modal-header">
          <h2 id="sign-modal-title" className="modal-title fs-3">
            Det går inte att ta bort klassen
          </h2>
          <button type="button" className="btn-close" aria-label={TranslationService.get('common.close')} onClick={handleOnClose}></button>
        </div>
        <div className="modal-body pt-0">
          <div className="mb-3">
            <p>
              <strong>
                {selectedParticipation?.name}, {selectedParticipation?.school?.name}
              </strong>{' '}
              har rapporterat in poäng och kan därför inte tas bort.
            </p>
            <p>Kontakta Västtrafiks projektledare för att få hjälp med att ta bort klassen.</p>
          </div>
          <div className="d-grid gap-3">
            <button type="button" className="btn btn-primary" onClick={handleOnClose}>
              Ok
            </button>
          </div>
        </div>
      </div>
    );
  }

  function renderDeleteModalContent() {
    return (
      <div className="modal-content">
        <div className="modal-header">
          <h2 id="sign-modal-title" className="modal-title fs-3">
            Ta bort klass
          </h2>
          <button type="button" className="btn-close" aria-label={TranslationService.get('common.close')} onClick={handleOnClose}></button>
        </div>
        <div className="modal-body pt-0">
          <div className="mb-3">
            Vill du ta bort{' '}
            <strong>
              {selectedParticipation?.name}, {selectedParticipation?.school?.name}
            </strong>
            ?
          </div>
          <div className="d-grid d-md-flex gap-3">
            <button type="button" className="btn btn-secondary flex-grow-1 flex-basis-0 order-2 order-md-1" onClick={handleOnClose}>
              Avbryt
            </button>
            <button type="button" className="btn btn-primary flex-grow-1 flex-basis-0 order-1 order-md-2" onClick={handleConfirmDeleteClick}>
              Ta bort
            </button>
          </div>
        </div>
      </div>
    );
  }

  function renderRemoveTeacherModalContent(teacher: IUser) {
    return (
      <div className="modal-content">
        <div className="modal-header">
          <h2 id="remove-teacher-modal-title" className="modal-title fs-3">
            Ta bort pedagog
          </h2>
          <button type="button" className="btn-close" aria-label={TranslationService.get('common.close')} onClick={handleOnClose}></button>
        </div>
        <div className="modal-body pt-0">
          <div className="mb-3">
            Vill du ta bort{' '}
            <strong>
              {teacher.firstName} {teacher.lastName}
            </strong>
            ?
          </div>
          <div className="d-grid d-md-flex gap-3">
            <button type="button" className="btn btn-secondary flex-grow-1 flex-basis-0 order-2 order-md-1" onClick={handleOnClose}>
              Avbryt
            </button>
            <button type="button" className="btn btn-primary flex-grow-1 flex-basis-0 order-1 order-md-2" onClick={async () => await handleRemoveTeacherClick(teacher)}>
              Ta bort
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      {saveStatus === Status.notperformed && (getParticipationStatus === Status.loading || getTeachersStatus === Status.loading) && <Spinner />}
      {(getParticipationStatus === Status.failed || getTeachersStatus === Status.failed) && <div>Det gick inte att hämta information om klassen. Försök igen senare.</div>}
      {(saveStatus !== Status.notperformed || (getParticipationStatus === Status.success && getTeachersStatus === Status.success)) && (
        <>
          <Breadcrumb
            items={[
              { text: 'Mina sidor', url: '/' },
              ...(viewerRole === Role.municipalityAdministrator ? [{ text: participation?.school?.municipality?.name ?? '', url: `/kommuner/${participation?.school?.municipality?.gid}` }] : []),
              { text: ParticipationHelper.getFullName(participation), url: `/klasser/${participationId}` },
              { text: 'Klassuppgifter', url: undefined },
            ]}
          />
          <PageIntroduction heading="Klassuppgifter" />
          {removedTeacher && (
            <InformationBox
              type={InformationBoxType.Success}
              headingElement={<strong>{removedTeacher.firstName + ' ' + removedTeacher.lastName + ' har tagits bort'}</strong>}
              className="mb-4 mb-md-5"
              closeCallback={() => setRemovedTeacher(undefined)}
            />
          )}
          <InformationBox
            type={InformationBoxType.Default}
            headingElement={<h2 className="h4 mb-2">Pedagoger</h2>}
            textElement={
              <>
                {teachers?.length === 0 ? (
                  <div className="mb-2">Pedagog saknas</div>
                ) : (
                  <ul className="list-unstyled mb-2">
                    {teachers?.map((x, i, row) => (
                      <li key={x.emailAddress} className={i + 1 === row.length ? '' : 'mb-2'}>
                        <div className="row w-100 align-items-center">
                          <div className="col-10 text-break">
                            <div>
                              {x.firstName} {x.lastName}
                            </div>
                            <div>{x.emailAddress}</div>
                            {x.phoneNumber && <div>{x.phoneNumber}</div>}
                          </div>
                          <div className="col-2 p-0 text-end">
                            {getParticipationData && viewerRole === Role.municipalityAdministrator && (
                              <button type="button" className="btn btn-tertiary btn-icon-left btn-icon-trash" onClick={() => setSelectedTeacher(x)}>
                                <span className="visually-hidden">Ta bort {x.firstName + ' ' + x.lastName}</span>
                              </button>
                            )}
                          </div>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
                <Link
                  to={`/klasser/${participationId}/lagg-till-pedagog`}
                  className="btn btn-secondary"
                  onClick={() => AnalyticsService.addEvent(viewerRole === Role.teacher ? AnalyticsService.PaEgnaBenTeacher : AnalyticsService.PaEgnaBenAdmin, 'Klick på `Lägg till pedagog`')}
                >
                  Lägg till pedagog
                </Link>
              </>
            }
            className="mb-4 mb-md-5"
          />
          {selectedParticipation && (
            <>
              <FocusTrap isVisible={!!selectedParticipation}>
                <div className="modal d-block fade show" aria-labelledby="sign-modal-title" aria-modal="true" role="dialog">
                  <div className="modal-dialog modal-dialog-centered">{selectedParticipation.legReports.length > 0 ? renderPreventDeleteModalContent() : renderDeleteModalContent()}</div>
                </div>
              </FocusTrap>
              <div className="modal-backdrop fade show"></div>
            </>
          )}
          {selectedTeacher && (
            <>
              <FocusTrap isVisible={!!selectedTeacher}>
                <div className="modal d-block fade show" aria-labelledby="remove-teacher-modal-title" aria-modal="true" role="dialog">
                  <div className="modal-dialog modal-dialog-centered">{renderRemoveTeacherModalContent(selectedTeacher)}</div>
                </div>
              </FocusTrap>
              <div className="modal-backdrop fade show"></div>
            </>
          )}
          <form id="form">
            <fieldset>
              <legend className="visually-hidden">Formulär för klassuppgifter</legend>
              <div>
                <ValidationSummary id="form-validation-summary" validationSummaryErrors={validationSummaryErrors} />
                <TextFieldFormGroup
                  ref={nameRef}
                  labelText="Namn"
                  required={true}
                  hideRequiredAsterisk={true}
                  inputId="name-input"
                  type="text"
                  serverValidationName="name"
                  state={[nameState, setNameState]}
                />
                <TextFieldFormGroup
                  ref={numberOfStudentsRef}
                  labelText="Antal elever"
                  required={true}
                  hideRequiredAsterisk={true}
                  inputId="number-of-students-input"
                  type="number"
                  serverValidationName="numberOfStudents"
                  helpTextElement={<span>Antalet elever används för att räkna ut klassens medelpoäng. På så sätt tävlar klasserna på lika villkor oavsett antal elever.</span>}
                  state={[numberOfStudentsState, setNumberOfStudentsState]}
                />
                <SelectSchool
                  ref={schoolRef}
                  municipalityGid={viewerRole === Role.municipalityAdministrator ? participation?.school?.municipalityGid : undefined}
                  inputId="school-select"
                  showNewSchoolRadio={false}
                  showPostalInfo={false}
                  state={selectSchoolState}
                  onChange={(value) => {
                    if (value) {
                      setSelectSchoolState((ps) => ({ ...ps, value }));
                    }
                  }}
                  onKeyUp={(e) => {
                    if (e === SelectSchoolField.mode) {
                      setSelectSchoolState((ps) => ({
                        ...ps,
                        validationInfo: new ValidationInfo(),
                      }));
                    }
                    if (e === SelectSchoolField.existingSchool) {
                      setSelectSchoolState((ps) => ({
                        ...ps,
                        validationInfo: new ValidationInfo(ps.validationInfo.prefix, ps.validationInfo.errors, {
                          ...ps.validationInfo.children,
                          existingSchool: new ValidationInfo(),
                        }),
                      }));
                    }
                    if (e === SelectSchoolField.name) {
                      setSelectSchoolState((ps) => ({
                        ...ps,
                        validationInfo: new ValidationInfo(ps.validationInfo.prefix, ps.validationInfo.errors, {
                          ...ps.validationInfo.children,
                          schoolName: new ValidationInfo(),
                        }),
                      }));
                    }
                    if (e === SelectSchoolField.streetAddress) {
                      setSelectSchoolState((ps) => ({
                        ...ps,
                        validationInfo: new ValidationInfo(ps.validationInfo.prefix, ps.validationInfo.errors, {
                          ...ps.validationInfo.children,
                          schoolStreetAddress: new ValidationInfo(),
                        }),
                      }));
                    }
                    if (e === SelectSchoolField.postalCode) {
                      setSelectSchoolState((ps) => ({
                        ...ps,
                        validationInfo: new ValidationInfo(ps.validationInfo.prefix, ps.validationInfo.errors, {
                          ...ps.validationInfo.children,
                          schoolPostalCode: new ValidationInfo(),
                        }),
                      }));
                    }
                    if (e === SelectSchoolField.city) {
                      setSelectSchoolState((ps) => ({
                        ...ps,
                        validationInfo: new ValidationInfo(ps.validationInfo.prefix, ps.validationInfo.errors, {
                          ...ps.validationInfo.children,
                          schoolCity: new ValidationInfo(),
                        }),
                      }));
                    }
                  }}
                />
              </div>
              <div className="d-grid d-md-flex gap-3">
                <button type="button" className="btn btn-primary flex-grow-1 flex-basis-0" onClick={handleSaveButtonClick}>
                  Spara ändringar
                </button>
              </div>

              {getParticipationData && viewerRole === Role.municipalityAdministrator && (
                <button type="button" className="btn btn-tertiary btn-icon-left btn-icon-trash mt-3" onClick={() => setSelectedParticipation(participation)}>
                  Ta bort klass
                </button>
              )}

              {deleteParticipationStatus.status === Status.failed && (
                <Alert show={true} heading="Kunde inte ta bort klassen" closable={false} className="mb-4" />
              )}
              {saveStatus === Status.failed && saveErrorCode !== 400 && (
                <Alert show={true} heading="Kunde inte ändra klassuppgifter" closable={false} className="mt-3" />
              )}
            </fieldset>
          </form>
        </>
      )}
    </div>
  );
}

export default EditParticipation;